import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { Auth0Context } from '@auth0/auth0-react'
import { AuthTokenManager } from '../helpers/commonUtills'

const STORAGE_KEY = 'demo-selected-role'
const LOCAL_TOKEN_KEY = 'local-access-token'

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'demo'
const MOCK_JWT_URL = process.env.REACT_APP_MOCK_JWT_URL || 'http://localhost:9001'

const ROLE_TO_MOCK_JWT = {
  system_admin: 'sa',
  facility_admin: 'fa',
  facility_user: 'fu',
}

async function fetchLocalToken(roleKey) {
  const mockJwtRole = ROLE_TO_MOCK_JWT[roleKey]
  if (!mockJwtRole) throw new Error(`No mock-jwt mapping for role: ${roleKey}`)
  const res = await fetch(`${MOCK_JWT_URL}/token?user=${encodeURIComponent(mockJwtRole)}`, {
    method: 'POST',
  })
  if (!res.ok) throw new Error(`mock-jwt token request failed: ${res.status}`)
  const body = await res.json()
  return body.access_token
}

const DEMO_USER = {
  sub: 'demo|user-001',
  name: 'Demo User',
  email: 'demo.user@locumscope.com',
  picture: '',
  email_verified: true,
}

const DEMO_IDENTITY = {
  user_id: 'u1',
  email: 'demo.user@locumscope.com',
  first_name: 'Demo',
  last_name: 'User',
  full_name: 'Demo User',
  is_active: true,
  client_account_no: 'ACC-101',
  parentAccountNo: 'ACC-101',
  client_account_name: 'ForceMed Health System',
  account_name: 'ForceMed Health System',
  phone: '555-000-0000',
  tableau_subscription: true,
  specialties: ['Emergency Medicine', 'Hospitalist', 'Critical Care Medicine'],
}

export const ROLE_CONFIGS = {
  system_admin: {
    label: 'System Admin',
    title: 'Local System Admin',
    personas: ['super_admin'],
    userAccess: {
      candidates: true,
      scheduling: true,
      spend_management_dashboard: true,
      spend_management_menu: true,
      locums_analytics: true,
      scheduled_report: true,
      landing_page: true,
      schedule_management: true,
      can_onboard_user: true,
    },
    mapping_permissions: {
      access_all_worksite: true,
      access_all_specialty: true,
    },
    facilities: [],
  },
  facility_admin: {
    label: 'Facility Admin',
    title: 'Demo Facility Admin',
    personas: ['admin'],
    userAccess: {
      candidates: true,
      scheduling: true,
      spend_management_dashboard: true,
      spend_management_menu: true,
      locums_analytics: true,
      scheduled_report: true,
      landing_page: true,
      schedule_management: true,
      can_onboard_user: true,
    },
    mapping_permissions: {
      access_all_worksite: false,
      access_all_specialty: false,
    },
    facilities: [
      { account_name: 'ForceMed Health System', facility_account_no: 'ACC-101' },
      { account_name: 'ForceMed Imperial Point', facility_account_no: 'ACC-101-WS1' },
      { account_name: 'ForceMed Coral Springs', facility_account_no: 'ACC-101-WS2' },
    ],
  },
  facility_user: {
    label: 'Facility User',
    title: 'Demo Facility User',
    personas: ['user'],
    userAccess: {
      candidates: true,
      scheduling: true,
      spend_management_dashboard: true,
      spend_management_menu: true,
      locums_analytics: true,
      scheduled_report: false,
      landing_page: true,
      schedule_management: true,
      can_onboard_user: false,
    },
    mapping_permissions: {
      access_all_worksite: false,
      access_all_specialty: false,
    },
    facilities: [
      { account_name: 'ForceMed Health System', facility_account_no: 'ACC-101' },
      { account_name: 'ForceMed Imperial Point', facility_account_no: 'ACC-101-WS1' },
      { account_name: 'ForceMed Coral Springs', facility_account_no: 'ACC-101-WS2' },
    ],
  },
}

const encodeJwtSegment = (obj) => {
  const json = JSON.stringify(obj)
  return btoa(json).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

const buildJwtPayload = (roleKey) => {
  const config = ROLE_CONFIGS[roleKey]
  if (!config) return null

  const now = Math.floor(Date.now() / 1000)
  return {
    ...DEMO_IDENTITY,
    title: config.title,
    personas: config.personas,
    userAccess: config.userAccess,
    mapping_permissions: config.mapping_permissions,
    facilities: config.facilities,
    exp: now + 86400,
    iat: now,
  }
}

const buildToken = (roleKey) => {
  const payload = buildJwtPayload(roleKey)
  if (!payload) return null

  return [
    encodeJwtSegment({ alg: 'none', typ: 'JWT' }),
    encodeJwtSegment(payload),
    'demo-signature',
  ].join('.')
}

export const DemoRoleContext = createContext({
  currentRole: null,
  setRole: () => {},
  roleLabel: '',
})

export const useDemoRole = () => useContext(DemoRoleContext)

const DemoAuthProvider = ({ children }) => {
  const [currentRole, setCurrentRole] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored && ROLE_CONFIGS[stored]) {
      if (AUTH_MODE === 'local') {
        const cached = sessionStorage.getItem(LOCAL_TOKEN_KEY)
        if (cached) {
          AuthTokenManager.set(cached)
          localStorage.setItem('sessionTime', JSON.stringify({ startTime: Date.now() }))
        }
      } else {
        const token = buildToken(stored)
        if (token) {
          AuthTokenManager.set(token)
          localStorage.setItem('sessionTime', JSON.stringify({ startTime: Date.now() }))
        }
      }
      return stored
    }
    return null
  })

  const applyRole = useCallback(async (roleKey) => {
    let token
    if (AUTH_MODE === 'local') {
      token = await fetchLocalToken(roleKey)
      sessionStorage.setItem(LOCAL_TOKEN_KEY, token)
    } else {
      token = buildToken(roleKey)
    }
    if (!token) return

    AuthTokenManager.set(token)
    sessionStorage.setItem(STORAGE_KEY, roleKey)
    localStorage.setItem('sessionTime', JSON.stringify({ startTime: Date.now() }))
    setCurrentRole(roleKey)
  }, [])

  useEffect(() => {
    const keepAlive = setInterval(() => {
      if (sessionStorage.getItem(STORAGE_KEY)) {
        localStorage.setItem('sessionTime', JSON.stringify({ startTime: Date.now() }))
      }
    }, 5 * 60 * 1000)
    return () => clearInterval(keepAlive)
  }, [])

  const getAccessTokenSilently = useCallback(async () => {
    const token = AuthTokenManager.get()
    return { access_token: token }
  }, [])

  const loginWithRedirect = useCallback(() => {
    window.location.href = '/demo/role-select'
  }, [])

  const logout = useCallback(() => {
    AuthTokenManager.remove()
    sessionStorage.removeItem(STORAGE_KEY)
    sessionStorage.removeItem(LOCAL_TOKEN_KEY)
    sessionStorage.removeItem('loginAuditFired')
    window.location.href = '/'
  }, [])

  const roleLabel = currentRole ? ROLE_CONFIGS[currentRole]?.label ?? '' : ''

  const auth0Value = useMemo(
    () => ({
      isAuthenticated: true,
      isLoading: false,
      user: DEMO_USER,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      getIdTokenClaims: async () => ({}),
      handleRedirectCallback: async () => ({}),
      error: undefined,
    }),
    [getAccessTokenSilently, loginWithRedirect, logout],
  )

  const roleValue = useMemo(
    () => ({
      currentRole,
      setRole: applyRole,
      roleLabel,
    }),
    [currentRole, applyRole, roleLabel],
  )

  return (
    <DemoRoleContext.Provider value={roleValue}>
      <Auth0Context.Provider value={auth0Value}>{children}</Auth0Context.Provider>
    </DemoRoleContext.Provider>
  )
}

DemoAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default DemoAuthProvider
